// Account-related actions for web app integrating latest API endpoints
// Integrated with API v4 account endpoints from latest commits (WUFOO-16850 through WUFOO-16857)
import { api } from '../services/api'

export const ACCOUNT_ACTIONS = {
  // Profile data
  GET_PROFILE_REQUEST: 'GET_PROFILE_REQUEST',
  GET_PROFILE_SUCCESS: 'GET_PROFILE_SUCCESS',
  GET_PROFILE_FAILURE: 'GET_PROFILE_FAILURE',

  // Usage data
  GET_USAGE_REQUEST: 'GET_USAGE_REQUEST',
  GET_USAGE_SUCCESS: 'GET_USAGE_SUCCESS',
  GET_USAGE_FAILURE: 'GET_USAGE_FAILURE',

  // Plan data
  GET_PLAN_REQUEST: 'GET_PLAN_REQUEST',
  GET_PLAN_SUCCESS: 'GET_PLAN_SUCCESS',
  GET_PLAN_FAILURE: 'GET_PLAN_FAILURE',

  // Billing data
  GET_BILLING_REQUEST: 'GET_BILLING_REQUEST',
  GET_BILLING_SUCCESS: 'GET_BILLING_SUCCESS',
  GET_BILLING_FAILURE: 'GET_BILLING_FAILURE',

  // Invoices data
  GET_INVOICES_REQUEST: 'GET_INVOICES_REQUEST',
  GET_INVOICES_SUCCESS: 'GET_INVOICES_SUCCESS',
  GET_INVOICES_FAILURE: 'GET_INVOICES_FAILURE',

  // Subdomain operations
  CHECK_SUBDOMAIN_REQUEST: 'CHECK_SUBDOMAIN_REQUEST',
  CHECK_SUBDOMAIN_SUCCESS: 'CHECK_SUBDOMAIN_SUCCESS',
  CHECK_SUBDOMAIN_FAILURE: 'CHECK_SUBDOMAIN_FAILURE',

  CHANGE_SUBDOMAIN_REQUEST: 'CHANGE_SUBDOMAIN_REQUEST',
  CHANGE_SUBDOMAIN_SUCCESS: 'CHANGE_SUBDOMAIN_SUCCESS',
  CHANGE_SUBDOMAIN_FAILURE: 'CHANGE_SUBDOMAIN_FAILURE',

  // Account type operations
  CHANGE_ACCOUNT_TYPE_REQUEST: 'CHANGE_ACCOUNT_TYPE_REQUEST',
  CHANGE_ACCOUNT_TYPE_SUCCESS: 'CHANGE_ACCOUNT_TYPE_SUCCESS',
  CHANGE_ACCOUNT_TYPE_FAILURE: 'CHANGE_ACCOUNT_TYPE_FAILURE',

  // Combined operations
  GET_ACCOUNT_DATA_REQUEST: 'GET_ACCOUNT_DATA_REQUEST',
  GET_ACCOUNT_DATA_SUCCESS: 'GET_ACCOUNT_DATA_SUCCESS',
  GET_ACCOUNT_DATA_FAILURE: 'GET_ACCOUNT_DATA_FAILURE',

  // Legacy compatibility
  VERIFY_EMAIL_REQUEST: 'VERIFY_EMAIL_REQUEST',
  VERIFY_EMAIL_SUCCESS: 'VERIFY_EMAIL_SUCCESS',
  VERIFY_EMAIL_FAILURE: 'VERIFY_EMAIL_FAILURE'
}

// Validate and normalise a subdomain value before sending to the API.
// Returns the lowercase subdomain string, or throws a descriptive Error.
const validateSubdomain = (subdomain) => {
  if (!subdomain || typeof subdomain !== 'string' || subdomain.length > 55) {
    throw new Error('Invalid subdomain: must be a string with max 55 characters')
  }
  if (!/^[a-z0-9]+$/i.test(subdomain)) {
    throw new Error('Subdomain must contain only alphanumeric characters')
  }
  return subdomain.toLowerCase()
}

// Individual API action creators

// Get account profile data - GET /api/v4/account/profile
export const getAccountProfile = () => async (dispatch) => {
  dispatch({ type: ACCOUNT_ACTIONS.GET_PROFILE_REQUEST })

  try {
    const { data } = await api.get('/account/profile')

    // Validate response structure based on Swagger spec
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid profile data received from API')
    }

    dispatch({
      type: ACCOUNT_ACTIONS.GET_PROFILE_SUCCESS,
      payload: data
    })
    return data
  } catch (error) {
    dispatch({
      type: ACCOUNT_ACTIONS.GET_PROFILE_FAILURE,
      payload: error.message
    })
    throw error
  }
}

// Note: getAccountUsage() and getAccountPlan() have been removed
// Usage and plan data now comes from getPlan() action which uses:
// - /plan, /plan/features, and /stats endpoints
// This provides more comprehensive and consistent data

// Get account billing data
export const getAccountBilling = () => async (dispatch) => {
  dispatch({ type: ACCOUNT_ACTIONS.GET_BILLING_REQUEST })

  try {
    const { data } = await api.get('/account/billing')
    dispatch({
      type: ACCOUNT_ACTIONS.GET_BILLING_SUCCESS,
      payload: data
    })
    return data
  } catch (error) {
    dispatch({
      type: ACCOUNT_ACTIONS.GET_BILLING_FAILURE,
      payload: error.message
    })
    throw error
  }
}

// Get account invoices data
// API returns invoices array and possible_years
export const getAccountInvoices = (year = new Date().getFullYear()) => async (dispatch) => {
  dispatch({ type: ACCOUNT_ACTIONS.GET_INVOICES_REQUEST })

  try {
    // Validate year parameter before sending to API
    const validYear = parseInt(year, 10)
    if (isNaN(validYear) || validYear < 2000 || validYear > 2100) {
      throw new Error('Invalid year parameter')
    }

    // API expects year parameter: /api/v4/account/invoices?year=2025
    // Response: { invoices: [...], possible_years: [2024, 2025, 2026] }
    const { data } = await api.get('/account/invoices', { params: { year: validYear }})

    dispatch({
      type: ACCOUNT_ACTIONS.GET_INVOICES_SUCCESS,
      payload: data  // Full response with invoices and possible_years
    })
    return data
  } catch (error) {
    dispatch({
      type: ACCOUNT_ACTIONS.GET_INVOICES_FAILURE,
      payload: error.message
    })
    throw error
  }
}

// Check subdomain availability - GET /api/v4/account/subdomain/check?subdomain=xxx
export const checkSubdomainAvailability = (subdomain) => async (dispatch) => {
  dispatch({ type: ACCOUNT_ACTIONS.CHECK_SUBDOMAIN_REQUEST })

  try {
    const cleanSubdomain = validateSubdomain(subdomain)

    const { data } = await api.get('/account/subdomain/check', { params: { subdomain: cleanSubdomain }})

    // Validate response structure based on Swagger spec
    if (!data || typeof data.available !== 'boolean') {
      throw new Error('Invalid subdomain check response from API')
    }

    dispatch({
      type: ACCOUNT_ACTIONS.CHECK_SUBDOMAIN_SUCCESS,
      payload: data
    })
    return data
  } catch (error) {
    dispatch({
      type: ACCOUNT_ACTIONS.CHECK_SUBDOMAIN_FAILURE,
      payload: error.message
    })
    throw error
  }
}

// Change account subdomain - PUT /api/v4/account/subdomain
export const changeSubdomain = (subdomain) => async (dispatch) => {
  dispatch({ type: ACCOUNT_ACTIONS.CHANGE_SUBDOMAIN_REQUEST })

  try {
    const cleanSubdomain = validateSubdomain(subdomain)

    const { data } = await api.put('/account/subdomain', { subdomain: cleanSubdomain })

    // PUT /account/subdomain returns { available, url, message } (SubdomainChangeModel).
    // The API first checks availability before attempting the change:
    //   available: true  → subdomain was free and the change was applied; url = new login URL
    //   available: false → subdomain was already taken or invalid; change was NOT applied
    if (!data) {
      throw new Error('Invalid subdomain change response from API')
    }

    dispatch({
      type: ACCOUNT_ACTIONS.CHANGE_SUBDOMAIN_SUCCESS,
      payload: data
    })

    if (data.available) {
      // Change was applied successfully
      dispatch({
        type: 'ADD_TOAST',
        payload: {
          type: 'success',
          message: data.message || 'Subdomain changed successfully!'
        }
      })
    } else if (data.message) {
      // Subdomain was taken or invalid — surface the API's message to the user
      dispatch({
        type: 'ADD_TOAST',
        payload: {
          type: 'error',
          message: data.message
        }
      })
    }

    return data
  } catch (error) {
    dispatch({
      type: ACCOUNT_ACTIONS.CHANGE_SUBDOMAIN_FAILURE,
      payload: error.message
    })

    // Show error message
    dispatch({
      type: 'ADD_TOAST',
      payload: {
        type: 'error',
        message: error.message || 'Failed to change subdomain.'
      }
    })

    throw error
  }
}

// Change account type - PUT /api/v4/account/type
export const changeAccountType = (accountType) => async (dispatch) => {
  dispatch({ type: ACCOUNT_ACTIONS.CHANGE_ACCOUNT_TYPE_REQUEST })

  try {
    // Validate account type (case-insensitive — transformed to title case before sending)
    if (!accountType || ![ 'business', 'consumer' ].includes(accountType.toLowerCase())) {
      throw new Error('Invalid account type: must be "Business" or "Consumer"')
    }

    // API expects title case: "Business" or "Consumer"
    const titleCaseAccountType = accountType.charAt(0).toUpperCase() + accountType.slice(1).toLowerCase()

    const { data } = await api.put('/account/type', { accountType: titleCaseAccountType }, { noDecamelize: true })

    // Validate response structure based on Swagger spec
    if (!data || !data.accountType) {
      throw new Error('Invalid account type change response from API')
    }

    dispatch({
      type: ACCOUNT_ACTIONS.CHANGE_ACCOUNT_TYPE_SUCCESS,
      payload: data
    })

    // Show success message
    dispatch({
      type: 'ADD_TOAST',
      payload: {
        type: 'success',
        message: 'Account type updated successfully!'
      }
    })

    return data
  } catch (error) {
    dispatch({
      type: ACCOUNT_ACTIONS.CHANGE_ACCOUNT_TYPE_FAILURE,
      payload: error.message
    })

    // Show error message
    dispatch({
      type: 'ADD_TOAST',
      payload: {
        type: 'error',
        message: error.message || 'Failed to change account type.'
      }
    })

    throw error
  }
}

// Combined action to get all account data
export const getAccountData = () => async (dispatch, getState) => {
  dispatch({ type: ACCOUNT_ACTIONS.GET_ACCOUNT_DATA_REQUEST })

  try {
    // Try to fetch all account data from API v4 endpoints
    // Note: usage and plan data now comes from getPlan() action which uses /plan, /plan/features, and /stats
    let profile, billing, invoices
    let apiErrors = []

    // Fetch all APIs in parallel, but handle errors individually
    const fetchWithErrorHandling = async (actionCreator, name) => {
      try {
        const result = await dispatch(actionCreator())
        return result
      } catch (error) {
        apiErrors.push(`${name}: ${error.message}`)
        return null
      }
    }

    // Start all requests in parallel so billing/invoices don't wait on profile
    const profilePromise = fetchWithErrorHandling(getAccountProfile, 'Profile')
    const billingPromise = fetchWithErrorHandling(getAccountBilling, 'Billing')
    const invoicesPromise = fetchWithErrorHandling(getAccountInvoices, 'Invoices')

    // Profile is the source of truth — await it first so a fast failure (blocked
    // request, instant error) shows the error state immediately without waiting
    // for billing/invoices to finish (which may have longer timeouts).
    profile = await profilePromise

    if (!profile) {
      const errorMessage = apiErrors.length > 0
        ? apiErrors.join('; ')
        : 'Failed to load account details'
      dispatch({
        type: ACCOUNT_ACTIONS.GET_ACCOUNT_DATA_FAILURE,
        payload: errorMessage
      })
      return
    }

    // Profile succeeded — collect billing and invoices (already in flight)
    [ billing, invoices ] = await Promise.all([ billingPromise, invoicesPromise ])

    // Billing and invoices failures are non-fatal — use safe empty defaults
    const defaultBilling = billing ?? {
      billingEmail: '',
      taxStatus: null,
      billingInfoAvailable: false
    }

    const defaultInvoices = invoices ?? {
      invoices: [],
      currencies: { '1': { symbol: '$', code: 'USD' }}
    }

    const defaultProfile = profile

    // Combine all data for the component (based on Swagger API v4 response structures)
    const combinedData = {
      accountInfo: {
        // From AccountProfile model
        name: defaultProfile.user?.username || 'User',
        email: defaultProfile.user?.email || '',
        subdomain: defaultProfile.subdomain || '',
        planName: getState().plan?.plan?.name || 'Free',
        accountType: defaultProfile.account_type || 'Business', // API sends title case
        status: defaultProfile.cancelled === 0 ? 'Active' : (defaultProfile.cancelled === 1 ? 'Cancelled' : 'Deleted'),
        company: defaultProfile.user?.company || '',
        timezone: defaultProfile.user?.timezone || 0,
        displayConsumerDesignation: defaultProfile.displayConsumerDesignation || false,
        isAccountOwner: defaultProfile.user?.isAccountOwner || false,
        isAdmin: defaultProfile.user?.isAdmin || false,
        domain: defaultProfile.domain || null  // Let component determine domain from flags
      },
      // Note: usageStats and planInfo are now provided by getPlan() action
      // which fetches from /plan, /plan/features, and /stats endpoints
      billingInfo: {
        // From /api/v4/account/billing
        billingEmail: defaultBilling.billingEmail || '',
        taxStatus: defaultBilling.taxStatus || null,
        billingInfoAvailable: defaultBilling.billingInfoAvailable || false,
        upcomingInvoice: defaultBilling.upcomingInvoice || null
      },
      bills: defaultInvoices.invoices || []
    }

    dispatch({
      type: ACCOUNT_ACTIONS.GET_ACCOUNT_DATA_SUCCESS,
      payload: combinedData
    })

    return combinedData
  } catch (error) {
    dispatch({
      type: ACCOUNT_ACTIONS.GET_ACCOUNT_DATA_FAILURE,
      payload: error.message
    })

    // Show error message
    dispatch({
      type: 'ADD_TOAST',
      payload: {
        type: 'error',
        message: 'Failed to load data. Please try again.'
      }
    })

    throw error
  }
}

export const verifyEmail = () => async (dispatch) => {
  dispatch({ type: ACCOUNT_ACTIONS.VERIFY_EMAIL_REQUEST })

  try {
    // TODO: Replace with actual API call
    // const response = await api.post('/account/verify-email')

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))

    dispatch({
      type: ACCOUNT_ACTIONS.VERIFY_EMAIL_SUCCESS,
      payload: { message: 'Verification email sent successfully!' }
    })

    // Show success toast
    dispatch({
      type: 'ADD_TOAST',
      payload: {
        type: 'success',
        message: 'Verification email sent! Please check your inbox.'
      }
    })

  } catch (error) {
    dispatch({
      type: ACCOUNT_ACTIONS.VERIFY_EMAIL_FAILURE,
      payload: error.message
    })

    // Show error toast
    dispatch({
      type: 'ADD_TOAST',
      payload: {
        type: 'error',
        message: 'Failed to send verification email. Please try again.'
      }
    })
  }
}

export const updateAccountInfo = (updateData) => async (dispatch) => {
  dispatch({ type: ACCOUNT_ACTIONS.UPDATE_ACCOUNT_INFO_REQUEST })

  try {
    // TODO: Replace with actual API call
    // const response = await api.put('/account/info', updateData)

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))

    dispatch({
      type: ACCOUNT_ACTIONS.UPDATE_ACCOUNT_INFO_SUCCESS,
      payload: updateData
    })

    dispatch({
      type: 'ADD_TOAST',
      payload: {
        type: 'success',
        message: 'Account information updated successfully!'
      }
    })

  } catch (error) {
    dispatch({
      type: ACCOUNT_ACTIONS.UPDATE_ACCOUNT_INFO_FAILURE,
      payload: error.message
    })

    dispatch({
      type: 'ADD_TOAST',
      payload: {
        type: 'error',
        message: 'Failed to update account information.'
      }
    })
  }
}
